import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Tabs, Typography, theme } from 'antd';
import { StatCell } from '@/components/common/show/StatCell';
import type { PersonDetailOut } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { CoverThumb } from '@/components/common/show/CoverThumb';
import { PERSON_SHOW_TABS } from '../constants';
import type { PersonShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<PersonShowTab, string> = {
  general: 'base.sections.basic_info',
  publications: 'user_catalog.common.tabs.publications',
  collection: 'base.sections.collection_info',
  search: 'user_catalog.common.tabs.search',
};

interface PersonShowHeaderProps {
  record: PersonDetailOut;
  /** Namespace supplies the role word shown under the name. */
  ns: string;
  activeTab: PersonShowTab;
  onTabChange: (key: string) => void;
}

export const PersonShowHeader = ({
  record,
  ns,
  activeTab,
  onTabChange,
}: PersonShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <CoverThumb url={record.cover_url} size={128} />

        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Long names wrap instead of pushing the tabs bar out. */}
          <Title
            level={3}
            style={{
              margin: 0,
              fontSize: token.fontSizeHeading3,
              overflowWrap: 'anywhere',
            }}
          >
            {record.name}
          </Title>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t(`${ns}.titles.show`)} · ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('user_catalog.common.show.publishers')}>
              {record.publishers?.length ?? 0}
            </StatCell>
            <StatCell label={t('base.fields.mod_status')}>
              <ModStatusTag value={record.mod_status} />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={PERSON_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
